import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const MOUNT_TYPES = [
  { id: 'fixed', label: 'Fixed Tilt', icon: '/' },
  { id: 'single_axis', label: 'Single-Axis Horizontal', icon: '~' },
  { id: 'single_axis_tilted', label: 'Single-Axis Tilted', icon: '~/' },
  { id: 'two_axis', label: 'Two-Axis', icon: '+' },
];

const PVSystemsView = () => {
  const [activeTab, setActiveTab] = useState('systems');
  const [systems, setSystems] = useState([]);
  const [modules, setModules] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [moduleSearch, setModuleSearch] = useState('');
  const [inverterSearch, setInverterSearch] = useState('');
  const [moduleMfrs, setModuleMfrs] = useState([]);
  const [inverterMfrs, setInverterMfrs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Datasheet scanner state
  const [showScanner, setShowScanner] = useState(false);
  const [scanType, setScanType] = useState('module');
  const [scanEngine, setScanEngine] = useState('auto');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const [scanFile, setScanFile] = useState(null);

  const handleScanUpload = async () => {
    if (!scanFile) return;
    setScanning(true);
    setScanError('');
    setScanResult(null);
    try {
      const formData = new FormData();
      formData.append('file', scanFile);
      formData.append('type', scanType);
      formData.append('engine', scanEngine);
      const res = await fetch(`${API}/api/scan-datasheet`, { method: 'POST', body: formData });
      const json = await res.json();
      if (res.ok) {
        setScanResult(json.extracted);
      } else {
        setScanError(json.error || 'Scan failed');
      }
    } catch (e) {
      setScanError(`Error: ${e.message}`);
    }
    setScanning(false);
  };

  const handleSaveScanResult = async () => {
    if (!scanResult) return;
    try {
      const endpoint = scanType === 'module' ? '/api/scan-datasheet/save-module' : '/api/scan-datasheet/save-inverter';
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scanResult),
      });
      if (res.ok) {
        setShowScanner(false);
        setScanResult(null);
        setScanFile(null);
        // Refresh the relevant list
        if (scanType === 'module') fetchModules(moduleSearch);
        else fetchInverters(inverterSearch);
      } else {
        const err = await res.json();
        setScanError(err.error || 'Save failed');
      }
    } catch (e) {
      setScanError(`Error: ${e.message}`);
    }
  };

  const fetchSystems = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/pv-systems`);
      if (res.ok) setSystems(await res.json());
    } catch {}
  }, []);

  const fetchModules = useCallback(async (q = '') => {
    try {
      const res = await fetch(`${API}/api/pv-modules?limit=50&q=${encodeURIComponent(q)}`);
      if (res.ok) setModules(await res.json());
    } catch {}
  }, []);

  const fetchInverters = useCallback(async (q = '') => {
    try {
      const res = await fetch(`${API}/api/pv-inverters?limit=50&q=${encodeURIComponent(q)}`);
      if (res.ok) setInverters(await res.json());
    } catch {}
  }, []);

  const fetchMfrs = useCallback(async () => {
    try {
      const [mRes, iRes] = await Promise.all([
        fetch(`${API}/api/pv-modules/manufacturers`),
        fetch(`${API}/api/pv-inverters/manufacturers`),
      ]);
      if (mRes.ok) setModuleMfrs(await mRes.json());
      if (iRes.ok) setInverterMfrs(await iRes.json());
    } catch {}
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSystems(), fetchModules(), fetchInverters(), fetchMfrs()])
      .finally(() => setLoading(false));
  }, [fetchSystems, fetchModules, fetchInverters, fetchMfrs]);

  const dbEmpty = modules.length === 0 && inverters.length === 0;

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>PV Systems</h1>
          <p className="def-list-subtitle">
            Solar PV system definitions — modules, inverters, mounting, string configuration, and losses.
            Powered by pvlib + CEC database.
          </p>
        </div>
        <button className="btn-accent" onClick={() => { setShowScanner(true); setScanResult(null); setScanError(''); setScanFile(null); }}>
          Scan Datasheet
        </button>
      </div>

      {/* Tabs */}
      <div className="charts-tabs" style={{ padding: '0 16px' }}>
        {[
          { id: 'systems', label: `System Definitions (${systems.length})` },
          { id: 'modules', label: `Module Database (${modules.length}${dbEmpty ? ' — run seed' : '+'})` },
          { id: 'inverters', label: `Inverter Database (${inverters.length}${dbEmpty ? ' — run seed' : '+'})` },
        ].map(tab => (
          <button
            key={tab.id}
            className={`charts-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Setup notice if DB is empty */}
      {dbEmpty && (
        <div className="ref-info-box" style={{ margin: 16 }}>
          <div className="ref-info-header"><span>CEC Database Not Yet Loaded</span></div>
          <div className="ref-info-notes">
            To load the CEC module and inverter databases (30,000+ modules, 5,000+ inverters), run:
            <br/><code style={{ color: 'var(--cenec-green)', fontSize: 12 }}>
              cd ~/NRG/backend && python3 pv_engine.py
            </code>
            <br/>This requires pvlib to be installed:
            <code style={{ color: 'var(--cenec-green)', fontSize: 12 }}>
              {' '}sudo bash ~/NRG/backend/install-pvlib.sh
            </code>
          </div>
        </div>
      )}

      {/* System definitions tab */}
      {activeTab === 'systems' && (
        <div style={{ padding: 16 }}>
          {systems.length === 0 ? (
            <div className="node-hub-empty">
              No PV system definitions yet. Create one by selecting a module, inverter, and configuring the mounting system.
            </div>
          ) : (
            <div className="def-card-grid">
              {systems.map(s => (
                <div key={s.id} className="def-card" style={{ borderColor: '#F59E0B' }}>
                  <div className="def-card-info">
                    <div className="def-card-name">{s.name}</div>
                    <div className="def-card-meta">
                      <span>{s.mount_type}</span>
                      <span>{s.system_capacity_kwp} kWp</span>
                      <span>DC/AC: {s.dc_ac_ratio}</span>
                    </div>
                    <div className="def-card-dims">
                      {s.module_name} + {s.inverter_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mount type overview */}
          <div className="node-hub-grid" style={{ marginTop: 24 }}>
            {MOUNT_TYPES.map(mt => (
              <div key={mt.id} className="node-hub-card" style={{ borderTopColor: '#F59E0B' }}>
                <div className="node-hub-card-header">
                  <span className="node-hub-icon" style={{ background: '#F59E0B', fontSize: 24 }}>{mt.icon}</span>
                  <div>
                    <div className="node-hub-card-title" style={{ color: '#F59E0B', fontSize: 14 }}>{mt.label}</div>
                  </div>
                </div>
                <div className="node-hub-stats">
                  <div className="node-hub-stat">
                    <span className="node-hub-stat-value" style={{ color: '#F59E0B' }}>
                      {systems.filter(s => s.mount_type === mt.id).length}
                    </span>
                    <span className="node-hub-stat-label">Systems</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modules tab */}
      {activeTab === 'modules' && (
        <div style={{ padding: 16 }}>
          <div className="heatmap-controls" style={{ marginBottom: 12 }}>
            <label>Search:</label>
            <input
              value={moduleSearch}
              onChange={(e) => { setModuleSearch(e.target.value); fetchModules(e.target.value); }}
              placeholder="Module name or manufacturer..."
              style={{ width: 300 }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
              {moduleMfrs.length} manufacturers
            </span>
          </div>

          <div className="conductor-table-wrap">
            <table className="conductor-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Manufacturer</th>
                  <th>Technology</th>
                  <th>Power (W)</th>
                  <th>Vmp (V)</th>
                  <th>Imp (A)</th>
                  <th>Voc (V)</th>
                  <th>Isc (A)</th>
                  <th>Eff %</th>
                </tr>
              </thead>
              <tbody>
                {modules.map(m => (
                  <tr key={m.id}>
                    <td className="conductor-name">{m.name}</td>
                    <td>{m.manufacturer}</td>
                    <td><span className="conductor-type-badge oh">{m.technology || '?'}</span></td>
                    <td className="mono" style={{ fontWeight: 600, color: '#F59E0B' }}>{m.stc_power_w}</td>
                    <td className="mono">{m.v_mp?.toFixed(1)}</td>
                    <td className="mono">{m.i_mp?.toFixed(2)}</td>
                    <td className="mono">{m.v_oc?.toFixed(1)}</td>
                    <td className="mono">{m.i_sc?.toFixed(2)}</td>
                    <td className="mono">{m.efficiency_pct?.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inverters tab */}
      {activeTab === 'inverters' && (
        <div style={{ padding: 16 }}>
          <div className="heatmap-controls" style={{ marginBottom: 12 }}>
            <label>Search:</label>
            <input
              value={inverterSearch}
              onChange={(e) => { setInverterSearch(e.target.value); fetchInverters(e.target.value); }}
              placeholder="Inverter name or manufacturer..."
              style={{ width: 300 }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
              {inverterMfrs.length} manufacturers
            </span>
          </div>

          <div className="conductor-table-wrap">
            <table className="conductor-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Manufacturer</th>
                  <th>Type</th>
                  <th>AC Power (W)</th>
                  <th>MPPT Low (V)</th>
                  <th>MPPT High (V)</th>
                  <th>Vdc Max (V)</th>
                  <th>Eff %</th>
                </tr>
              </thead>
              <tbody>
                {inverters.map(inv => (
                  <tr key={inv.id}>
                    <td className="conductor-name">{inv.name}</td>
                    <td>{inv.manufacturer}</td>
                    <td><span className={`conductor-type-badge ${inv.inverter_type === 'central' ? 'oh' : 'ug'}`}>{inv.inverter_type}</span></td>
                    <td className="mono" style={{ fontWeight: 600, color: '#3B82F6' }}>{inv.paco_w?.toLocaleString()}</td>
                    <td className="mono">{inv.mppt_low_v?.toFixed(0)}</td>
                    <td className="mono">{inv.mppt_high_v?.toFixed(0)}</td>
                    <td className="mono">{inv.vdcmax_v?.toFixed(0)}</td>
                    <td className="mono">{inv.efficiency_pct?.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Datasheet Scanner Modal */}
      {showScanner && (
        <div className="modal-overlay" onClick={() => setShowScanner(false)}>
          <div className="modal-content" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
            <h3>Scan Datasheet</h3>
            <p className="form-hint">Upload a manufacturer PDF or image. AI extracts all parameters automatically.</p>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select value={scanType} onChange={e => setScanType(e.target.value)}>
                  <option value="module">Solar Module</option>
                  <option value="inverter">Inverter</option>
                </select>
              </div>
              <div className="form-group">
                <label>AI Engine</label>
                <select value={scanEngine} onChange={e => setScanEngine(e.target.value)}>
                  <option value="auto">Auto (Claude, then Ollama)</option>
                  <option value="claude">Claude API</option>
                  <option value="ollama">Ollama / Mistral (local)</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Datasheet File (PDF, PNG, JPG)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => setScanFile(e.target.files[0])} />
            </div>

            {scanError && <div className="download-msg error">{scanError}</div>}

            {!scanResult && (
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setShowScanner(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleScanUpload} disabled={!scanFile || scanning}>
                  {scanning ? 'Scanning with AI...' : 'Extract Parameters'}
                </button>
              </div>
            )}

            {/* Extracted results */}
            {scanResult && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ color: 'var(--cenec-green)', marginBottom: 8 }}>
                  Extracted: {scanResult.name || 'Unknown'}
                  {scanResult._engine && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>via {scanResult._engine}</span>}
                </h3>

                <div className="conductor-table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
                  <table className="conductor-table">
                    <thead><tr><th>Parameter</th><th>Value</th></tr></thead>
                    <tbody>
                      {Object.entries(scanResult)
                        .filter(([k]) => !k.startsWith('_'))
                        .map(([k, v]) => (
                          <tr key={k}>
                            <td style={{ fontWeight: 500 }}>{k}</td>
                            <td className="mono">{v === null ? <span style={{ color: 'var(--text-muted)' }}>{'—'}</span> : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="form-actions" style={{ marginTop: 12 }}>
                  <button className="btn-secondary" onClick={() => { setScanResult(null); setScanFile(null); }}>Scan Another</button>
                  <button className="btn-primary" onClick={handleSaveScanResult}>
                    Save to Database
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PVSystemsView;
